'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';

/**
 * Captures a `?promo=CODE` query param from ANY visited public page into
 * localStorage, so a marketing link (homepage holiday CTA, the dedicated offer
 * page, an email/ad/social link — any source) carries the code through to
 * checkout where it auto-fills + auto-applies. localStorage (not session) is
 * deliberate: a marketing journey routinely crosses tabs and sessions (click an
 * email link in the morning, book that night), and the code's own server-side
 * validity window is the real gate — the checkout apply silently no-ops an
 * expired/invalid code — so we don't impose an arbitrary client-side TTL. The
 * captured intent is cleared once it's spent (booking submitted) or explicitly
 * rejected (guest removes the applied code) by checkout-form.
 *
 * Mounted once in the public [locale] layout. Renders nothing.
 */
const PENDING_PROMO_KEY = 'pendingPromoCode';

export function PromoCapture() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const raw = searchParams.get('promo');
    if (!raw) return;
    const code = raw.trim().toUpperCase().slice(0, 64);
    if (!code) return;
    try {
      localStorage.setItem(PENDING_PROMO_KEY, code);
    } catch {
      /* localStorage unavailable (private mode quota, etc.) — ignore */
    }
  }, [searchParams]);

  return null;
}
